export const buildWeeklyReview = ({ 
    todos = [], 
    roachEntries = [], 
    weekStart, 
    weekEnd 
}) => {

    if (!weekStart || !weekEnd) {
        return {
            weekNumber: 1,
            score: 0,
            stats: { total: 0, done: 0, planned: 0, cancelled: 0 },
            trend: Array(7).fill(0),
            bestDay: "-",
            weakDay: "-",
            objectives: {},
            roach: { avgScore: 0, type: "Unknown Survivor" },
            insights: [],
            signals: { focus: 0, consistency: 0, discipline: 0, resilience: 0 },
            weaknesses: ["No data available"],
        };
    }

    const weekStartDate = new Date(weekStart);
    weekStartDate.setHours(0, 0, 0, 0);

    const weekEndDate = new Date(weekEnd);
    weekEndDate.setHours(23, 59, 59, 999);

    // Filter using todoDate only (as per your confirmation)
    const weekTodos = todos.filter(t => {
        if (!t.todoDate) return false;
        const tDate = new Date(t.todoDate);
        return tDate >= weekStartDate && tDate <= weekEndDate;
    });

    const weekRoach = roachEntries.filter(r => {
        if (!r.createdAt) return false;
        const rDate = new Date(r.createdAt);
        return rDate >= weekStartDate && rDate <= weekEndDate;
    });

    // console.log(`🔍 Week Filter: ${weekTodos.length} / ${todos.length} todos | Range: ${weekStart} → ${weekEnd}`);

    const total = weekTodos.length;
    const done = weekTodos.filter((t) => t.status === "Done").length;
    const cancelled = weekTodos.filter((t) => t.status === "Cancelled").length;
    const planned = weekTodos.filter((t) => t.status === "Planned").length;

    const productivityScore = total ? Math.round((done / total) * 100) : 0;

    // OBJECTIVES
    const objectiveMap = {};
    weekTodos.forEach((t) => {
        const key = t.objective || "Unknown";
        objectiveMap[key] = (objectiveMap[key] || 0) + 1;
    });

    // TREND
    const trend = Array(7).fill(0);
    weekTodos.forEach((t) => {
        if (!t.todoDate) return;
        const dayIndex = new Date(t.todoDate).getDay();
        const index = dayIndex === 0 ? 6 : dayIndex - 1;

        if (t.status === "Done") trend[index] += 15;
        if (t.status === "Planned") trend[index] += 8;
        if (t.status === "Cancelled") trend[index] -= 5;
    });

    const normalizedTrend = trend.map((v) => Math.max(0, Math.min(100, v)));

    const daysList = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
    const bestDayIndex = normalizedTrend.indexOf(Math.max(...normalizedTrend));
    const weakDayIndex = normalizedTrend.indexOf(Math.min(...normalizedTrend));

    const bestDay = normalizedTrend.every(v => v === 0) ? "-" : daysList[bestDayIndex];
    const weakDay = normalizedTrend.every(v => v === 0) ? "-" : daysList[weakDayIndex];

    // ROACH
    const avgRoachScore = weekRoach.length
        ? Number((weekRoach.reduce((acc, curr) => acc + (curr.avgScore || 0), 0) / weekRoach.length).toFixed(1))
        : 0;

    const latestRoach = weekRoach[weekRoach.length - 1] || null;

    // SIGNALS
    const focusScore = Math.min(100, Math.round(
        ((objectiveMap.Build || 0) + (objectiveMap.Focus || 0) + (objectiveMap.Learning || 0)) / Math.max(total, 1) * 100
    ));

    const consistencyScore = Math.min(100, Math.round((done / Math.max(done + cancelled, 1)) * 100));
    const disciplineScore = Math.min(100, Math.round((done / Math.max(total, 1)) * 100));
    const resilienceScore = Math.min(100, Math.round((avgRoachScore / 9) * 100));

    // WEAKNESSES & INSIGHTS
    const weaknesses = [];
    if (cancelled >= 3) weaknesses.push("High distraction frequency");
    if ((objectiveMap.Random || 0) >= 4) weaknesses.push("Too many random objectives");
    if (focusScore < 45) weaknesses.push("Low deep-work focus");
    if (consistencyScore < 50) weaknesses.push("Weak consistency pattern");
    if (Number(avgRoachScore) < 4) weaknesses.push("Behavior stability dropping");
    if (!weaknesses.length) weaknesses.push("No major weaknesses detected");

    const insights = [
        productivityScore >= 70 ? "Strong execution momentum detected." : "Execution consistency needs improvement.",
        cancelled >= 3 ? "High distraction signals observed." : "Distraction levels under control.",
        latestRoach?.type ? `Behavior pattern: ${latestRoach.type}` : "No behavior assessment available.",
    ];

    return {
        weekNumber: 1,
        score: productivityScore,
        stats: { total, done, planned, cancelled },
        trend: normalizedTrend,
        bestDay,
        weakDay,
        objectives: objectiveMap,
        roach: {
            avgScore: avgRoachScore,
            type: latestRoach?.type || "Unknown Survivor",
        },
        signals: {
            focus: focusScore,
            consistency: consistencyScore,
            discipline: disciplineScore,
            resilience: resilienceScore,
        },
        weaknesses,
        insights,
    };
};